<script lang="ts">
  import { onMount } from "svelte"
  import TableStepLayout from "@kontextwerk/web-sdk/ui/layout/TableStepLayout.svelte"
  import { previewCancelReplanWorkOrders } from "../../../../../../../../../api/kehrblatt/schedule-events"
  import type {
    CancelEventsStepOutput,
    CancelReplanInit,
    CancelReplanWorkflowEvent,
  } from "../../../../_logic/cancel-replan-contract"
  import {
    applyCancelReplanPreviewTargets,
    buildCancelReplanRows,
    setCancelReplanRowRemoved,
  } from "../../../../_logic/rows"
  import {
    cancelReplanEmployeeLabel,
    createCancelTargetFilters,
    filterCancelTargetRows,
    type CancelTargetFilterState,
  } from "../../_shared/_logic/filters"
  import BatchFilterBar from "../../../../../../../_shared/_modules/batch-filter-bar"
  import {
    batchDateRangeInputValue,
    hasUsefulBatchDateRange,
    hasUsefulBatchSearch,
    hasUsefulBatchSelectOptions,
    normalizeBatchDateRange,
    uniqueBatchOptions,
  } from "../../../../../../../_shared/_logic/filter-values"
  import CancelTargetsTable from "./_modules/cancel-targets-table"
  import CancelAppointmentsAction from "./_modules/cancel-appointments-action"
  import type {
    CancelAppointmentsPhase,
    CancelAppointmentsResult,
  } from "./actions/cancelAppointments"
  import { buildCancelAppointmentsIntent } from "./build-cancel-appointments-intent"
  import { cancelAppointmentsOperations } from "./operations"

  let {
    init,
    output,
    readOnly = false,
    onEvent,
    onError,
  }: {
    init: CancelReplanInit
    output?: CancelEventsStepOutput
    readOnly?: boolean
    onEvent: (event: CancelReplanWorkflowEvent) => void | Promise<void>
    onError: (message: string | null) => void
  } = $props()

  const eventById = $derived(new Map(init.events.map((event) => [event.id, event])))
  const workOrderById = $derived(new Map(init.workOrders.map((order) => [order.id, order])))
  const workOrderRowById = $derived(new Map((init.sourceRows ?? []).map((row) => [row.id, row])))
  const propertyById = $derived(new Map(init.properties.map((property) => [property.id, property])))
  const initialRows = () =>
    output?.rows ??
    buildCancelReplanRows({
      events: init.events,
      workOrderById,
      workOrderRowById,
      propertyById,
    })

  let rows = $state(initialRows())
  let baselineRows = $state(initialRows())
  let tableFilters = $state<CancelTargetFilterState>(createCancelTargetFilters())
  let cancelPhase = $state<CancelAppointmentsPhase>("idle")
  let loading = $state(false)
  const busy = $derived(cancelPhase !== "idle")
  const activeRows = $derived(rows.filter((row) => !row.removed))
  const visibleRows = $derived(filterCancelTargetRows(rows, tableFilters))
  const employeeOptions = $derived(
    uniqueBatchOptions(activeRows.map(cancelReplanEmployeeLabel), "Alle Mitarbeiter"),
  )
  const appointmentDateValues = $derived(
    activeRows.map(
      (row) =>
        row.event?.start_at ??
        (row.event as { slot_start_at?: string | null } | null)?.slot_start_at,
    ),
  )
  const cancelIntent = $derived(buildCancelAppointmentsIntent({ rows, loading }))

  async function loadPreview() {
    if (output?.rows.length) {
      rows = output.rows
      baselineRows = output.rows

      return
    }

    loading = true
    rows = initialRows()
    baselineRows = rows

    if (!rows.length) {
      loading = false

      return
    }

    try {
      const orderIds = rows.map((row) => row.workOrder?.id).filter(Boolean) as string[]

      if (!orderIds.length) return

      const eventMap = new Map(init.events.map((event) => [event.id, event]))
      const preview = await previewCancelReplanWorkOrders({
        organization_id: init.organizationId,
        work_order_ids: orderIds,
      })
      rows = applyCancelReplanPreviewTargets({
        rows,
        targets: preview.items,
        eventById: eventMap,
      })
      baselineRows = rows
    } catch (error) {
      console.error("cancel_replan_preview_failed", error)
    } finally {
      loading = false
    }
  }

  onMount(() => {
    void loadPreview()
  })

  const removeRow = (rowId: string) => {
    if (readOnly) return

    rows = setCancelReplanRowRemoved(rows, rowId, true)
  }

  const restoreRow = (rowId: string) => {
    if (readOnly) return

    rows = setCancelReplanRowRemoved(rows, rowId, false)
  }

  const resetRow = (rowId: string) => {
    if (readOnly) return

    const baselineRow = baselineRows.find((row) => row.id === rowId)

    if (!baselineRow) return

    rows = rows.map((row) => (row.id === rowId ? { ...baselineRow, removed: false } : row))
  }

  const updateFilter = (fieldId: string, value: unknown) => {
    tableFilters = {
      ...tableFilters,
      [fieldId]: fieldId === "dateRange" ? normalizeBatchDateRange(value) : String(value ?? ""),
    }
  }

  const handleCancelResult = async (result: CancelAppointmentsResult) => {
    await onEvent({ type: "cancel.completed", output: result.output })
  }
</script>

<div class="step-body">
  <CancelAppointmentsAction
    intent={cancelIntent}
    operations={cancelAppointmentsOperations}
    onSubmissionResult={handleCancelResult}
    onPhaseChange={(phase) => (cancelPhase = phase)}
  >
    {#snippet children(primaryAction)}
      <TableStepLayout
        titleActions={readOnly ? null : primaryAction}
        title="Zu stornierende Termine"
        count={visibleRows.filter((row) => !row.removed).length}
        description="Prüfen Sie die Termine, die storniert und anschließend neu geplant werden sollen."
      >
        {#snippet filters()}
          <BatchFilterBar
            fields={[
              {
                id: "search",
                type: "text",
                placeholder: "Suche",
                value: tableFilters.search,
                enabled: hasUsefulBatchSearch(activeRows.length),
              },
              {
                id: "employee",
                type: "select",
                placeholder: "Mitarbeiter",
                value: tableFilters.employee,
                options: employeeOptions,
                enabled: hasUsefulBatchSelectOptions(employeeOptions, activeRows.length),
              },
              {
                id: "dateRange",
                type: "dateRange",
                placeholder: "Terminzeitraum",
                value: batchDateRangeInputValue(tableFilters.dateRange),
                enabled: hasUsefulBatchDateRange(appointmentDateValues, activeRows.length),
              },
            ]}
            disabled={busy || loading || readOnly}
            onChange={updateFilter}
          />
        {/snippet}

        <CancelTargetsTable
          rows={visibleRows}
          unitContactContexts={init.unitContactContexts ?? []}
          {readOnly}
          onRemoveRow={removeRow}
          onRestoreRow={restoreRow}
          onResetRow={resetRow}
        />

        {#snippet footer()}
          {#if !readOnly}
            <div class="step-actions">
              {@render primaryAction()}
            </div>
          {/if}
        {/snippet}
      </TableStepLayout>
    {/snippet}
  </CancelAppointmentsAction>
</div>

<style lang="less">
  .step-body {
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
  }

  .step-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.65rem;
  }
</style>
